import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  FiHome,
  FiBarChart2,
  FiUsers,
  FiLayers,
  FiShoppingCart,
  FiCheckCircle,
  FiChevronRight,
  FiSettings,
  FiGift,
  FiLogOut,
  FiChevronDown,
  FiMail,
  FiShield,
  FiSliders,
  FiClock,
  FiAward,
  FiHelpCircle,
  FiTag,
  FiUserCheck,
} from "react-icons/fi";
import { TbMoneybag } from "react-icons/tb";
import { MdOutlineStackedBarChart } from "react-icons/md";
import { useAuth } from "../../context/AuthContext";

const menuSections = [
  {
    title: "OVERVIEW",
    items: [
      { label: "Dashboard", icon: FiHome, to: "/" },
      { label: "Financial Analytics", icon: FiBarChart2, to: "/analytics" },
      { label: "Game Reports", icon: MdOutlineStackedBarChart, to: "/game-analytics" },
    ],
  },
  {
    title: "PLAYERS & COMPLIANCE",
    items: [
      { label: "Members", icon: FiUsers, to: "/users" },
      { label: "KYC Verification", icon: FiCheckCircle, to: "/kyc-verification" },
      { label: "AMOE Mail-In", icon: FiMail, to: "/amoe-management" },
      { label: "Self-Exclusion", icon: FiShield, to: "/self-exclusion" },
    ],
  },
  {
    title: "WALLETS & CASHFLOW",
    items: [
      { label: "Coin Purchases", icon: TbMoneybag, to: "/recharge-list" },
      { label: "Prize Redemptions", icon: FiShoppingCart, to: "/withdrawal-list" },
      { label: "Balance Adjustments", icon: FiSliders, to: "/bonus-and-panelty" },
      { label: "Adjustment History", icon: FiClock, to: "/bonus-and-panelty-history" },
    ],
  },
  {
    title: "PROMOTIONS & REWARDS",
    items: [
      { label: "Promotional Bonuses", icon: FiGift, to: "/bonus-management" },
      { label: "Gift Codes", icon: FiTag, to: "/gift-codes" },
      { label: "Gift Users", icon: FiUsers, to: "/gift-used-user" },
      { label: "Milestones", icon: FiAward, to: "/milestones" },
    ],
  },
  {
    title: "SUPPORT & SYSTEM",
    items: [
      {
        label: "Support Tickets",
        icon: FiHelpCircle,
        children: [
          { label: "Pending Issues", to: "/pending-issues" },
          { label: "Resolved Issues", to: "/resolved-issues" },
        ],
      },
      {
        label: "Settings",
        icon: FiSettings,
        children: [
          { label: "Deposit Bonus Rules", to: "/settings/deposit/bonus" },
          { label: "State Restrictions", to: "/settings/states" },
          { label: "Country Restrictions", to: "/settings/countries" },
          { label: "General Settings", to: "/settings/general" },
        ],
      },
      { label: "API Bet History", icon: FiLayers, to: "/api-management" },
      { label: "Admin Accounts", icon: FiUserCheck, to: "/admin" },
    ],
  },
];

const Sidebar = ({ isOpen, onClose, isCollapsed }) => {
  const [openGroups, setOpenGroups] = useState({ "Support Tickets": true, Settings: false });
  const { user, logout } = useAuth();

  const toggleGroup = (label) => {
    setOpenGroups((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const getInitials = (name) => {
    if (!name) return "AD";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 md:hidden transition-opacity duration-200"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed z-40 inset-y-0 left-0 
          ${isCollapsed ? "md:w-20" : "md:w-72"} w-72
          bg-[#071322]
          border-r border-slate-800/80
          shadow-2xl
          transform transition-all duration-300 ease-in-out
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0
          flex flex-col
        `}
      >
        {/* Brand / Logo Section */}
        <div className={`h-20 flex items-center ${isCollapsed ? "justify-center px-2" : "px-6"} border-b border-slate-800/80 bg-[#09172a]`}>
          <NavLink to="/" className="flex items-center gap-3 overflow-hidden" onClick={onClose}>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center font-black text-slate-950 text-xl shadow-lg shadow-amber-500/20 flex-shrink-0">
              TV
            </div>
            {!isCollapsed && (
              <div className="flex flex-col min-w-0">
                <span className="font-extrabold text-sm tracking-wide text-white uppercase truncate">
                  Treasure Vault
                </span>
                <span className="text-[10px] tracking-widest text-amber-400 font-semibold uppercase">
                  Sweepstakes Casino
                </span>
              </div>
            )}
          </NavLink>
        </div>

        {/* Menu Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6 scrollbar-thin scrollbar-thumb-slate-700">
          {menuSections.map((section, idx) => (
            <div key={idx} className="space-y-1">
              {!isCollapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-bold tracking-wider text-slate-400 uppercase">
                  {section.title}
                </div>
              )}

              {section.items.map((item) => {
                const Icon = item.icon;
                const hasChildren = !!item.children?.length;
                const isOpenGroup = openGroups[item.label];

                if (!hasChildren) {
                  return (
                    <NavLink
                      key={item.label}
                      to={item.to}
                      title={isCollapsed ? item.label : undefined}
                      className={({ isActive }) =>
                        `group flex items-center ${isCollapsed ? "justify-center px-2 py-3" : "gap-3 px-3.5 py-2.5"} rounded-xl transition-all duration-200 ${
                          isActive
                            ? "bg-gradient-to-r from-amber-500/20 to-orange-500/10 text-amber-400 border border-amber-500/30 shadow-md shadow-amber-500/5 font-semibold"
                            : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
                        }`
                      }
                      onClick={onClose}
                    >
                      <Icon className={`text-lg flex-shrink-0 transition-transform group-hover:scale-110`} />
                      {!isCollapsed && (
                        <>
                          <span className="text-xs font-medium tracking-wide flex-1 truncate">
                            {item.label}
                          </span>
                          <FiChevronRight className="text-xs text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </>
                      )}
                    </NavLink>
                  );
                }

                return (
                  <div key={item.label} className="space-y-1">
                    <button
                      onClick={() => toggleGroup(item.label)}
                      title={isCollapsed ? item.label : undefined}
                      className={`group w-full flex items-center ${isCollapsed ? "justify-center px-2 py-3" : "gap-3 px-3.5 py-2.5"} rounded-xl transition-all duration-200 ${
                        isOpenGroup && !isCollapsed
                          ? "bg-slate-800/50 text-slate-200"
                          : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-100"
                      }`}
                    >
                      <Icon className="text-lg flex-shrink-0 transition-transform group-hover:scale-110" />
                      {!isCollapsed && (
                        <>
                          <span className="text-xs font-medium tracking-wide flex-1 text-left truncate">
                            {item.label}
                          </span>
                          <FiChevronDown
                            className={`text-xs transition-transform duration-200 text-slate-500 ${
                              isOpenGroup ? "rotate-180 text-amber-400" : ""
                            }`}
                          />
                        </>
                      )}
                    </button>

                    {isOpenGroup && !isCollapsed && (
                      <div className="ml-5 pl-3 border-l border-slate-800 space-y-1 mt-1">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.label}
                            to={child.to}
                            className={({ isActive }) =>
                              `group flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all duration-200 ${
                                isActive
                                  ? "bg-amber-500/15 text-amber-400 font-semibold border-l-2 border-amber-400"
                                  : "text-slate-400 hover:bg-slate-800/40 hover:text-slate-200"
                              }`
                            }
                            onClick={onClose}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-amber-400 transition-colors" />
                            <span className="truncate">{child.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </nav>

        {/* User Profile Footer */}
        <div className="p-3 border-t border-slate-800/80 bg-[#09172a]/60">
          {!isCollapsed ? (
            <div className="bg-slate-900/90 rounded-xl p-3 border border-slate-800/80 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center text-slate-950 font-bold text-xs flex-shrink-0 shadow-sm">
                  {getInitials(user?.name || user?.username || "Admin")}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-xs text-white truncate">
                    {user?.name || user?.username || "Admin"}
                  </p>
                  <p className="text-[10px] text-slate-400 truncate">
                    {user?.email || "admin@treasurevaultarcade.com"}
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  logout();
                  onClose();
                }}
                title="Logout"
                className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
              >
                <FiLogOut size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              title="Logout"
              className="w-full flex items-center justify-center p-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-colors"
            >
              <FiLogOut size={16} />
            </button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
